import React, { useEffect, useRef, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { Bell, X, Eye, Volume2, VolumeX, Bike, Store } from "lucide-react";
import { OrderActions } from "./BellOrderActions";
import { B2BDocsDialog } from "./B2BDocsDialog";
import { curSym } from "../../utils/cur";

// 🔔 Online shop order notifier — ding + voice announcement (follows app language)
// Polls /api/shop/orders-feed every 15s. With `dineIn` it also polls restaurant table orders (merged bell).
// With `bell` prop it also renders a bell button + dropdown panel (POS).
const TEXTS = {
  zh: { title: "🛒 网店新订单", remind: "⏰ 订单还没确认！", speak: (t) => `网店收到订单，${t} 美元`, lang: "zh-CN",
        panel: "订单通知", online: "网店订单", dinein: "堂食桌台", none: "没有待处理订单", view: "查看", delivery: "配送", pickup: "自取", pending: "待确认", processing: "处理中" },
  es: { title: "🛒 Nuevo pedido online", remind: "⏰ ¡Pedido sin confirmar!", speak: (t) => `Nuevo pedido en línea por ${t} dólares`, lang: "es-ES",
        panel: "Pedidos", online: "Pedidos online", dinein: "Mesas", none: "Sin pedidos pendientes", view: "Ver", delivery: "Delivery", pickup: "Pickup", pending: "Pendiente", processing: "Procesando" },
  en: { title: "🛒 New online order", remind: "⏰ Order not confirmed yet!", speak: (t) => `New online order for ${t} dollars`, lang: "en-US",
        panel: "Orders", online: "Online orders", dinein: "Tables", none: "No pending orders", view: "View", delivery: "Delivery", pickup: "Pickup", pending: "Pending", processing: "Processing" },
  pt: { title: "🛒 Novo pedido online", remind: "⏰ Pedido sem confirmar!", speak: (t) => `Novo pedido online de ${t} dólares`, lang: "pt-BR",
        panel: "Pedidos", online: "Pedidos online", dinein: "Mesas", none: "Sem pedidos pendentes", view: "Ver", delivery: "Delivery", pickup: "Retirada", pending: "Pendente", processing: "Processando" },
};

const getCfg = () => TEXTS[localStorage.getItem("app_lang") || "zh"] || TEXTS.zh;

export const playDing = (style = "crisp") => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    if (style === "loud") {
      // 🔊 Loud alarm: 3 bursts of high square-wave two-tone, cuts through noisy stores
      for (let i = 0; i < 3; i++) {
        [1200, 1600].forEach((freq, j) => {
          const t0 = now + i * 0.45 + j * 0.18;
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.frequency.value = freq;
          osc.type = "square";
          gain.gain.setValueAtTime(0, t0);
          gain.gain.linearRampToValueAtTime(0.6, t0 + 0.015);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.17);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.2);
        });
      }
      setTimeout(() => ctx.close && ctx.close(), 1800);
      return;
    }
    [880, 1320].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = "sine";
      gain.gain.setValueAtTime(0, now + i * 0.15);
      gain.gain.linearRampToValueAtTime(0.35, now + i * 0.15 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.15 + 0.4);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + i * 0.15);
      osc.stop(now + i * 0.15 + 0.45);
    });
    setTimeout(() => ctx.close && ctx.close(), 800);
  } catch { /* audio blocked */ }
};

// 🗣️ Voice announcement — uses device TTS voice matching app language;
// falls back to bundled MP3 (works on POS terminals without Chinese TTS voices)
let voicesCache = [];
const loadVoices = () => {
  try {
    if (!window.speechSynthesis) return;
    voicesCache = window.speechSynthesis.getVoices() || [];
    if (voicesCache.length === 0) {
      window.speechSynthesis.onvoiceschanged = () => {
        voicesCache = window.speechSynthesis.getVoices() || [];
      };
    }
  } catch { /* no tts */ }
};
loadVoices();

const playAnnouncementMp3 = (remind) => {
  try {
    const lang = localStorage.getItem("app_lang") || "zh";
    const key = ["zh", "es", "en", "pt"].includes(lang) ? lang : "zh";
    const audio = new Audio(`/sounds/new_order_${key}${remind ? "_remind" : ""}.mp3`);
    audio.volume = 1.0;
    audio.play().catch(() => { /* autoplay blocked */ });
  } catch { /* audio blocked */ }
};

export const speak = (total, remind = false) => {
  try {
    const cfg = getCfg();
    if (voicesCache.length === 0) loadVoices();
    const prefix = cfg.lang.split("-")[0].toLowerCase();
    const voice = voicesCache.find((v) => (v.lang || "").toLowerCase().replace("_", "-").startsWith(prefix));
    if (!window.speechSynthesis || !voice) {
      playAnnouncementMp3(remind);
      return;
    }
    const u = new SpeechSynthesisUtterance(cfg.speak(total.toFixed(2)));
    u.lang = cfg.lang;
    u.voice = voice;
    u.rate = 0.95;
    u.onerror = () => playAnnouncementMp3(remind);
    window.speechSynthesis.speak(u);
  } catch { playAnnouncementMp3(remind); }
};

export default function OnlineOrderNotifier({ bell = false, shifted = false, dineIn = false }) {
  const bootstrappedRef = useRef(false);
  const knownIdsRef = useRef(new Set());
  const sysSettingsRef = useRef(null);
  const lastAlertRef = useRef({}); // order id -> last alert timestamp (ms), for 3-min repeat reminders
  const mutedRef = useRef(localStorage.getItem("online_orders_muted") === "1");
  const [items, setItems] = useState([]);
  const [muted, setMuted] = useState(mutedRef.current);
  const [alertEnabled, setAlertEnabled] = useState(true);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  useEffect(() => {
    let cancelled = false;
    const REMIND_MS = 3 * 60 * 1000;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/shop/orders-feed`);
        if (cancelled) return;
        const feed = r.data?.items || [];
        setItems(feed);
        const prefs = r.data?.notify || {};
        setAlertEnabled(prefs.alert_enabled !== false);
        const cfg = getCfg();
        const now = Date.now();
        if (!bootstrappedRef.current) {
          bootstrappedRef.current = true;
          feed.forEach((o) => { knownIdsRef.current.add(o.id); lastAlertRef.current[o.id] = now; });
        } else {
          const newOnes = feed.filter((o) => !knownIdsRef.current.has(o.id));
          if (newOnes.length > 0) {
            newOnes.forEach((o) => { knownIdsRef.current.add(o.id); lastAlertRef.current[o.id] = now; });
            // 🖨️ Auto-print Pedido A5 for new wholesale orders (atomic claim = single copy)
            if (r.data?.auto_print_pedido) {
              newOnes.filter((o) => o.channel === "wholesale").forEach(async (o) => {
                try {
                  const c = await axios.post(`${API}/shop/orders/${o.id}/claim-auto-print`);
                  if (c.data?.claimed && c.data.order) {
                    if (!sysSettingsRef.current) {
                      sysSettingsRef.current = (await axios.get(`${API}/settings/system`)).data || {};
                    }
                    const { printPedidoA5 } = await import("@/utils/wholesaleInvoice");
                    printPedidoA5(c.data.order, sysSettingsRef.current);
                    toast.info(`🖨️ Pedido #${o.order_no} impreso automáticamente / 已自动打印拣货单`);
                  }
                } catch { /* another client claimed it */ }
              });
            }
            // 🖨️ Auto-print 80mm ticket for new retail (online shop) orders — same atomic claim, one copy per order
            const _myDev = (() => { try { return localStorage.getItem('sellox_device_id') || ''; } catch { return ''; } })();
            if (r.data?.auto_print_retail && (!r.data.auto_print_device_id || r.data.auto_print_device_id === _myDev)) {
              newOnes.filter((o) => o.channel !== "wholesale").forEach(async (o) => {
                try {
                  const c = await axios.post(`${API}/shop/orders/${o.id}/claim-auto-print`);
                  if (c.data?.claimed && c.data.order) {
                    if (!sysSettingsRef.current) sysSettingsRef.current = (await axios.get(`${API}/settings/system`)).data || {};
                    const rates = (await axios.get(`${API}/exchange-rates`)).data || {};
                    const [{ printReceipt80mm }, { shopOrderToReceipt }, { docPrintOpts }] = await Promise.all([import("@/utils/receiptPrinter"), import("@/utils/shopOrderReceipt"), import("@/utils/printerPrefs")]);
                    printReceipt80mm(shopOrderToReceipt(c.data.order), sysSettingsRef.current, rates, null, docPrintOpts('ticket'));
                    toast.info(`🖨️ Pedido #${o.order_no} impreso automáticamente / 已自动打印`);
                  }
                } catch { /* another client claimed it */ }
              });
            }
            if (prefs.alert_enabled !== false) {
              if (!mutedRef.current) playDing(prefs.sound || "crisp");
              newOnes.forEach((o) => {
                toast.success(`${o.channel === "wholesale" ? "🏬 MAYOR · " : ""}${cfg.title} · ${curSym()}${o.total.toFixed(2)}`, {
                  description: `${o.customer_name || ""} · #${o.order_no}`,
                  duration: 10000,
                });
                if (prefs.voice !== false && !mutedRef.current) speak(o.total);
              });
            }
          }
        }
        // ⏰ Repeat reminder: pending (unconfirmed) orders ring again every 3 min
        const stale = feed.filter((o) => {
          if (o.status !== "pending") { delete lastAlertRef.current[o.id]; return false; }
          const created = o.created_at ? new Date(o.created_at).getTime() : now;
          if (now - created > 2 * 60 * 60 * 1000) return false; // ignore orders older than 2h
          const last = lastAlertRef.current[o.id] || 0;
          return now - last >= REMIND_MS;
        });
        if (stale.length > 0 && !mutedRef.current && prefs.alert_enabled !== false) {
          stale.forEach((o) => { lastAlertRef.current[o.id] = now; });
          playDing(prefs.sound || "crisp");
          toast.warning(`${getCfg().remind} (${stale.length})`, {
            description: stale.map((o) => `#${o.order_no} · ${curSym()}${o.total.toFixed(2)}`).join("  |  "),
            duration: 12000,
          });
          if (prefs.voice !== false) speak(stale[0].total, true);
        }
      } catch { /* offline — retry next tick */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // 🍽️ Merged dine-in table orders (restaurants) — same bell, own section in the panel
  const [dinePending, setDinePending] = useState([]);
  const dineKnownRef = useRef(new Set());
  const dineSeededRef = useRef(false);
  useEffect(() => {
    if (!dineIn) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await axios.get(`${API}/restaurant/pending-table-orders`);
        if (cancelled) return;
        const list = r.data?.items || [];
        setDinePending(list);
        if (!dineSeededRef.current) {
          dineSeededRef.current = true;
          list.forEach((o) => dineKnownRef.current.add(o.id));
          return;
        }
        const newOnes = list.filter((o) => !dineKnownRef.current.has(o.id));
        if (newOnes.length > 0) {
          newOnes.forEach((o) => dineKnownRef.current.add(o.id));
          if (!mutedRef.current) playDing();
          newOnes.forEach((o) => {
            toast.success(`🍽️ Mesa ${o.table_number || "?"} · ${curSym()}${o.total.toFixed(2)}`, {
              description: `${o.customer_name || ""} · #${o.order_no}`,
              duration: 8000,
            });
          });
        }
      } catch { /* offline — retry next tick */ }
    };
    refresh();
    const id = setInterval(refresh, 10000);
    return () => { cancelled = true; clearInterval(id); };
  }, [dineIn]);

  // Not on the alert list and no dine-in duty → bell fully hidden
  if (!bell || (!alertEnabled && !dineIn)) return null;
  return <BellUI shifted={shifted} items={alertEnabled ? items : []} dineItems={dineIn ? dinePending : []} muted={muted} setMuted={setMuted} />;
}

function BellUI({ shifted, items, dineItems, muted, setMuted }) {
  const [open, setOpen] = useState(false);
  // 🛠️ Workbench: full orders + settings for in-POS order handling (permission-gated)
  const [fullOrders, setFullOrders] = useState({});
  const [canTake, setCanTake] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [docsOrder, setDocsOrder] = useState(null);
  const [ctx, setCtx] = useState(null);
  const refreshFull = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/shop/orders`);
      const map = {};
      (r.data || []).forEach(o => { map[o.id] = o; });
      setFullOrders(map);
      setCanTake(true);
    } catch (e) {
      if (e.response?.status === 403) setCanTake(false);
    }
  }, []);
  useEffect(() => {
    if (!open) return;
    refreshFull();
    if (!ctx) {
      Promise.all([
        axios.get(`${API}/settings/system`).catch(() => ({ data: {} })),
        axios.get(`${API}/exchange-rates`).catch(() => ({ data: {} })),
      ]).then(([s, r]) => setCtx({ settings: s.data || {}, rates: r.data || {} }));
    }
  }, [open, refreshFull]); // eslint-disable-line react-hooks/exhaustive-deps
  const defaultLeft = () => Math.max(8, window.innerWidth - 48 - (shifted ? 80 : 16));
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("online_bell_pos") || "null");
      if (saved && saved.x >= 0 && saved.x < window.innerWidth - 24 && saved.y >= 0 && saved.y < window.innerHeight - 24) return saved;
    } catch { /* ignore */ }
    return { x: defaultLeft(), y: 210 }; // below the header AND the search row (Turno / Price-check buttons stay clickable — y=150 still covered quick-price-btn at 1920px)
  });
  const dragRef = useRef(null);

  const onPointerDown = (e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const orig = { ...pos };
    let moved = false;
    const onMove = (ev) => {
      const dx = ev.clientX - startX, dy = ev.clientY - startY;
      if (!moved && Math.abs(dx) + Math.abs(dy) > 6) moved = true;
      if (moved) {
        setPos({
          x: Math.min(Math.max(4, orig.x + dx), window.innerWidth - 52),
          y: Math.min(Math.max(4, orig.y + dy), window.innerHeight - 52),
        });
      }
    };
    const onUp = (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (moved) {
        const dx = ev.clientX - startX, dy = ev.clientY - startY;
        const final = {
          x: Math.min(Math.max(4, orig.x + dx), window.innerWidth - 52),
          y: Math.min(Math.max(4, orig.y + dy), window.innerHeight - 52),
        };
        localStorage.setItem("online_bell_pos", JSON.stringify(final));
      } else {
        setOpen((o) => !o);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const cfg = getCfg();
  const pendingCount = items.filter((o) => o.status === "pending").length + (dineItems?.length || 0);
  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem("online_orders_muted", next ? "1" : "0");
      return next;
    });
  };
  const panelOnLeft = pos.x > window.innerWidth / 2;

  return (
    <div ref={dragRef} className="fixed z-[60]" style={{ left: pos.x, top: pos.y, touchAction: "none" }} data-testid="online-orders-notifier">
      <button
        onPointerDown={onPointerDown}
        className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-colors cursor-grab active:cursor-grabbing
          ${pendingCount > 0
            ? "bg-emerald-500 hover:bg-emerald-600 text-white animate-pulse"
            : "bg-slate-700/80 hover:bg-slate-600 text-slate-300"}`}
        data-testid="online-orders-bell"
        title={cfg.panel}
      >
        <Bell className="w-6 h-6" />
        {pendingCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold border-2 border-slate-900" data-testid="online-orders-count">
            {pendingCount}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute top-14 ${panelOnLeft ? "right-0" : "left-0"} w-80 max-h-[60vh] overflow-y-auto bg-slate-900 border border-emerald-500/40 rounded-xl shadow-2xl`} data-testid="online-orders-panel">
          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700 bg-emerald-500/10">
            <h3 className="text-sm font-bold text-emerald-200">🔔 {cfg.panel} ({items.length + (dineItems?.length || 0)})</h3>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { toggleMute(); if (muted) playDing(); }}
                className={`p-1 rounded hover:bg-slate-700 ${muted ? "text-slate-500" : "text-emerald-400"}`}
                data-testid="online-orders-mute-toggle"
              >
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" data-testid="online-orders-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {(dineItems?.length || 0) > 0 && (
            <div data-testid="bell-dinein-section">
              <div className="px-3 py-1.5 bg-orange-500/10 border-b border-slate-800 text-xs font-bold text-orange-300">🍽️ {cfg.dinein} ({dineItems.length})</div>
              <ul className="divide-y divide-slate-800">
                {dineItems.map((o) => (
                  <li key={o.id} className="px-3 py-2 hover:bg-slate-800/50" data-testid={`dinein-row-${o.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-orange-300 font-bold">🍽️ Mesa {o.table_number || "?"}</span>
                        <span className="text-emerald-400 font-semibold ml-2">{curSym()}{o.total.toFixed(2)}</span>
                      </div>
                      <a href="/admin/online-orders" className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-cyan-300 flex items-center gap-1" data-testid={`dinein-view-${o.id}`}>
                        <Eye className="w-3 h-3" /> {cfg.view}
                      </a>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{o.customer_name || "—"} · {o.item_count} · #{o.order_no}</div>
                    <div className="text-[10px] text-slate-500">{o.created_at ? new Date(o.created_at).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                  </li>
                ))}
              </ul>
              {items.length > 0 && <div className="px-3 py-1.5 bg-emerald-500/10 border-y border-slate-800 text-xs font-bold text-emerald-300">🛒 {cfg.online} ({items.length})</div>}
            </div>
          )}
          {items.length === 0 && (dineItems?.length || 0) === 0 ? (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">{cfg.none}</div>
          ) : (
            <ul className="divide-y divide-slate-800">
              {items.map((o) => (
                <li key={o.id} className="px-3 py-2 hover:bg-slate-800/50" data-testid={`online-order-row-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {o.delivery_method === "pickup"
                        ? <Store className="w-4 h-4 text-amber-400" />
                        : <Bike className="w-4 h-4 text-cyan-400" />}
                      <span className="text-white font-bold">#{o.order_no}</span>
                      {o.channel === "wholesale" && <span className="text-[9px] font-bold bg-amber-500/20 text-amber-300 rounded px-1 py-0.5" data-testid={`mayor-badge-${o.id}`}>MAYOR</span>}
                      <span className="text-emerald-400 font-semibold">{curSym()}{o.total.toFixed(2)}</span>
                    </div>
                    {canTake && fullOrders[o.id] ? (
                      <button
                        onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 font-bold ${expanded === o.id ? "bg-emerald-600 text-white" : "bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/40"}`}
                        data-testid={`online-order-handle-${o.id}`}
                      >
                        🛠️ {expanded === o.id ? "✕" : "Atender 接单"}
                      </button>
                    ) : (
                      <a
                        href="/admin/online-orders"
                        className="text-xs px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-cyan-300 flex items-center gap-1"
                        data-testid={`online-order-view-${o.id}`}
                      >
                        <Eye className="w-3 h-3" /> {cfg.view}
                      </a>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                    <span>{o.customer_name || "—"}</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${o.status === "pending" ? "bg-amber-500/20 text-amber-300" : "bg-blue-500/20 text-blue-300"}`}>
                      {o.status === "pending" ? cfg.pending : cfg.processing}
                    </span>
                    <span className="text-slate-500">{o.delivery_method === "pickup" ? cfg.pickup : cfg.delivery}</span>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {o.created_at ? new Date(o.created_at).toLocaleTimeString("es-VE", { hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                  {expanded === o.id && canTake && fullOrders[o.id] && (
                    <OrderActions key={`${o.id}-${fullOrders[o.id].order_status}-${fullOrders[o.id].payment_status}`}
                      order={fullOrders[o.id]} refresh={refreshFull}
                      onDocs={(ord) => setDocsOrder(ord)} />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <B2BDocsDialog open={!!docsOrder} onOpenChange={(v) => { if (!v) setDocsOrder(null); }}
        order={docsOrder ? { ...docsOrder, customer_name: docsOrder.customer_name || docsOrder.shipping_name } : null}
        settings={ctx?.settings} exchangeRates={ctx?.rates} storeInfo={null} />
    </div>
  );
}
